<template>
  <v-dialog
    :max-width="maxWidth"
    :fullscreen="isMobile"
    v-model="open"
    :retain-focus="retainFocus"
    :persistent="isMobile"
    no-click-animation
  >
    <template v-slot:activator="{ on, attrs }">
      <slot name="activator" :attrs="attrs" :on="on" />
    </template>

    <v-card
      class="d-flex closeable-dialog-container"
      :class="{
        'closeable-dialog-container--compact': isMobile,
      }"
    >
      <div
        style="margin-top: env(safe-area-inset-top)"
        class="d-flex align-center closeable-dialog-header"
      >
        <slot name="header"></slot>
        <close-button @click="toggleDialog(false)" />
      </div>
      <div class="d-flex closeable-dialog-content">
        <slot> Content </slot>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue"
import { mdiClose, mdiGift } from "@mdi/js"
import { useIsBreakpointInNames } from "features/common/vuetify/useVuetify"
import CloseButton from "features/common/ui/CloseButton.vue"

export default defineComponent({
  name: "CloseableDialog",
  components: { CloseButton },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    hash: {
      type: String,
      required: true,
    },
    maxWidth: {
      type: String,
      default: "",
    },
    retainFocus: {
      type: Boolean,
      default: true,
    },
    disableHash: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    if (this.disableHash) return
    if (window.location.hash === "#" + this.hash) {
      this.open = true
      this.$emit("open")
    }
  },
  methods: {
    mdiClose() {
      return mdiClose
    },
    toggleDialog(open) {
      if (open) {
        if (!this.disableHash) {
          this.$router.push("#" + this.hash)
        }
        this.open = true
      } else {
        if (!this.disableHash) {
          const urlWithoutHash = window.location.href.replace(/#.*$/, "")
          window.history.replaceState(null, null, urlWithoutHash)
        }
        this.open = false
      }
    },
  },
  watch: {
    "$route.hash"(newHash, oldHash) {
      if (this.disableHash) return
      const hash = "#" + this.hash
      if (newHash === hash) {
        this.open = true
      } else if (oldHash === hash) {
        this.open = false
      }
    },
  },
  setup: function (props, { emit }) {
    const isMobile = useIsBreakpointInNames(["xs"])
    const open = ref(
      props.disableHash ? false : window.location.hash === props.hash
    )

    watch(
      () => props.value,
      (newValue) => {
        if (open.value !== newValue) open.value = newValue
      },
      { immediate: true }
    )

    watch(open, (newVal) => {
      setHashWithoutScrolling(newVal ? props.hash : "")
      emit(newVal ? "open" : "close")
      emit("input", newVal)
    })

    const setHashWithoutScrolling = (hash) => {
      if (props.disableHash) return
      const scrollV = document.scrollingElement.scrollTop
      window.location.hash = hash
      // has to be timeout 0, otherwise it doesn't work
      setTimeout(() => {
        document.scrollingElement.scrollTop = scrollV
      }, 0)
    }

    return {
      mdiGift,
      isMobile,
      open,
      hash: props.hash,
    }
  },
})
</script>

<style scoped>
.closeable-dialog-container {
  flex-direction: column;
  max-height: 90vh;
  padding: 32px;
}

.closeable-dialog-header {
  justify-content: space-between;
  align-items: start;
  margin-bottom: 16px;
}

.closeable-dialog-content {
  overflow: auto;
}

.v-card.closeable-dialog-container--compact {
  padding: 16px !important;
}
</style>
